<template>
  <div>
    <div
      data-link-target="maincontent"
      :data-focus-trigger="true"
      tabindex="0"
      class="skip-link absolute top-0 cursor-pointer pointer-events-auto"
      @keydown="focusOnTargetOnClick"
      @click="focusOnTargetOnClick"
    >
      <AtomLinkWithIcon class="text-lg flex overflow-hidden p-4 leading-none" :title="t_('skip-link')">
        {{ t_('skip-link') }}
      </AtomLinkWithIcon>
    </div>

    <div class="db-base grid grid-cols-12 antialiased bg-app-green-100">
      <!-- Control panel component to render admin tools when ?cp=true is added in the url -->
      <ClientOnly>
        <OrganismControlPanel />
      </ClientOnly>

      <span id="maincontent" tabindex="0" class="col-span-full row-start-2 xl:row-start-1" />
      <slot />
      <header class="relative z-50 col-span-full row-start-1 pointer-events-none">
        <OrganismMobileMenu class="w-full xs:visible pointer-events-auto lg:hidden" />
        <OrganismDesktopMenu class="hidden w-full pointer-events-auto lg:flex" />
      </header>
      <client-only>
        <OrganismFooter class="col-span-full row-start-3" />
        <MoleculeLoaderOverlay />
      </client-only>
    </div>
  </div>
</template>

<script setup lang="ts">
import { setupTranslationPrefixer } from '~/composables/util'
import { useOverlayStore } from '~/store/overlay'
import { useCookiebot } from '~/composables/cookiebot'

const overlayStore = useOverlayStore()
const { t_ } = setupTranslationPrefixer('base')
useCookiebot()

if (import.meta.client) {
  overlayStore.$subscribe((state) => {
    if (state.loader || state.mobileMenu) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  })
}

overlayStore.reset()

function focusOnTargetOnClick(event: Event) {
  const dataFocusTrigger = (event.currentTarget as HTMLElement).getAttribute('data-focus-trigger')
  const dataLinkTarget = (event.currentTarget as HTMLElement).getAttribute('data-link-target')
  const target = document.getElementById(dataLinkTarget)
  const isMouseClick = event.type === 'click'
  const isEnterKey = event.type === 'keydown' && (event as KeyboardEvent).key === 'Enter'

  if (dataFocusTrigger && target && (isMouseClick || isEnterKey)) {
    target.focus()
  }
}

useHead({
  meta: [
    { name: 'apple-mobile-web-app-title', content: 'Dierenbescherming' },
    { name: 'application-name', content: 'Dierenbescherming' },
    { name: 'msapplication-TileColor', content: '#dfdf00' },
    { name: 'theme-color', content: '#ffffff' },
  ],
  link: [
    { rel: 'shortcut icon', type: 'image/x-icon', href: '/favicons/DB/favicon.ico' },
    { rel: 'apple-touch-icon', sizes: '180x180', href: '/favicons/DB/apple-touch-icon.png' },
    { rel: 'mask-icon', color: '#dfdf00', href: '/favicons/DB/safari-pinned-tab.svg' },
    { rel: 'icon', sizes: '32x32', href: '/favicons/DB/favicon-32x32.png' },
    { rel: 'icon', sizes: '16x16', href: '/favicons/DB/favicon-16x16.png' },
    { rel: 'manifest', href: '/favicons/DB/site.webmanifest' },
  ],
})
</script>

<style lang="css">
.skip-link {
  clip-path: circle(1px at 0 0);
  z-index: 1000;
}

.skip-link:focus,
.skip-link:focus-visible {
  @apply relative;
  clip-path: unset;
}
</style>
